<template>
  <div class="sidebar-left">
    <div class="sidebar">

      <!-- Logged In User Profile Sidebar -->
      <user-profile-sidebar
        :shall-show-user-profile-sidebar="shallShowUserProfileSidebar"
        :profile-user-data="profileUserData"
        @close-sidebar="$emit('update:shall-show-user-profile-sidebar', false)"
      />

      <!-- Sidebar Content -->
      <div
        class="sidebar-content"
        :class="{'show': mqShallShowLeftSidebar}"
      >

        <!-- Sidebar close icon -->
        <span v-if="false" class="sidebar-close-icon">
          <feather-icon
            icon="XIcon"
            size="16"
            @click="$emit('update:mq-shall-show-left-sidebar', false)"
          />
        </span>

        <!-- Header -->
        <h3 class="chat-title">
          {{ $t('Messages') }}
        </h3>
        <div class="chat-fixed-search">
          <div class="d-flex justify-content-start align-items-center w-100">
            <!-- Search -->
            <b-input-group class="input-group-merge w-100">
              <b-input-group-prepend is-text>
                <feather-icon
                  icon="SearchIcon"
                  class="text-muted"
                />
              </b-input-group-prepend>
              <b-form-input
                v-model="searchQuery"
                :placeholder="$t('Search for messages')"
              />
            </b-input-group>
          </div>
        </div>

        <!-- Chat Tabs -->
        <div class="chat-tabs">
          <div
            class="chat-tab"
            :class="{ 'active': activeTab === '1v1' }"
            @click="activeTab = '1v1'"
          >
            {{ $t('1v1 Chat') }}
          </div>
          <div
            class="chat-tab"
            :class="{ 'active': activeTab === 'community' }"
            @click="activeTab = 'community'"
          >
            {{ $t('Community') }}
          </div>
        </div>

        <!-- Status Selector (Only for Community tab) -->
        <div v-if="activeTab === 'community'" class="status-selector" @click="$emit('open-mood-modal')">
          <span class="status-text">{{ currentMood ? `${currentMood.name} ${currentMood.emoji}` : $t('Set status...') }}</span>
          <button class="status-edit-btn">
            <feather-icon icon="EditIcon" size="14" />
            {{ $t('Edit') }}
          </button>
        </div>

        <!-- 1v1 Chat Filters (Most Recent | Filter by Mood) -->
        <div v-if="activeTab === '1v1'" class="chat-filters">
          <button
            type="button"
            class="chat-filter-btn"
            :class="{ 'active': chatSort === 'most_recent' }"
            @click="$emit('set-sort-most-recent')"
          >
            <feather-icon icon="ClockIcon" size="14" />
            {{ $t('Most Recent') }}
          </button>

          <b-dropdown
            variant="link"
            no-caret
            toggle-class="chat-filter-btn p-0"
            :class="['chat-filter-mood', { 'active': !!chatMoodId }]"
            menu-class="chat-mood-menu"
          >
            <template #button-content>
              <feather-icon icon="SmileIcon" size="14" />
              {{ selectedMoodLabel || $t('Filter by Mood') }}
            </template>
            <b-dropdown-item
              v-for="mood in moodOptions"
              :key="mood.id"
              :active="chatMoodId === mood.id"
              @click="$emit('set-mood-filter', mood.id)"
            >
              {{ mood.name }} {{ mood.emoji }}
            </b-dropdown-item>
            <b-dropdown-divider v-if="chatMoodId" />
            <b-dropdown-item v-if="chatMoodId" @click="$emit('set-mood-filter', null)">
              {{ $t('Clear') }}
            </b-dropdown-item>
          </b-dropdown>
        </div>

        <!-- ScrollArea: Chat & Contacts -->
        <vue-perfect-scrollbar
          :settings="perfectScrollbarSettings"
          class="chat-user-list-wrapper list-group scroll-area"
        >
          <!-- 1v1 Chats -->
          <ul v-if="activeTab === '1v1'" class="chat-users-list chat-list media-list">
            <chat-contact
              v-for="contact in filteredChatsContacts"
              :key="contact.id"
              :user="contact"
              tag="li"
              :class="{'active': activeChatContactId === contact.id}"
              is-chat-contact
              @click="$emit('open-chat', contact.id)"
            />
          </ul>

          <!-- Community List -->
          <ul v-else class="chat-users-list community-list media-list">
            <community-contact
              v-for="contact in filteredCommunityContacts"
              :key="contact.id"
              :user="contact"
              tag="li"
              @open-chat="$emit('open-chat', $event)"
            />
          </ul>
        </vue-perfect-scrollbar>
      </div>

    </div>
  </div>
</template>

<script>
import {
  BAvatar, BInputGroup, BInputGroupPrepend, BFormInput,
  BDropdown, BDropdownItem, BDropdownDivider,
} from 'bootstrap-vue'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import { ref, computed } from '@vue/composition-api'
import ChatContact from './ChatContact.vue'
import CommunityContact from './CommunityContact.vue'
import UserProfileSidebar from './UserProfileSidebar.vue'

export default {
  components: {

    // BSV
    BAvatar,
    BInputGroup,
    BInputGroupPrepend,
    BFormInput,
    BDropdown,
    BDropdownItem,
    BDropdownDivider,

    // 3rd party
    VuePerfectScrollbar,

    // SFC
    ChatContact,
    CommunityContact,
    UserProfileSidebar,
  },
  props: {
    chatsContacts: {
      type: Array,
      required: true,
    },
    communityContacts: {
      type: Array,
      default: () => [],
    },
    currentMood: {
      type: Object,
      default: null,
    },
    moodOptions: {
      type: Array,
      default: () => [],
    },
    chatSort: {
      type: String,
      default: null,
    },
    chatMoodId: {
      type: [Number, String],
      default: null,
    },
    shallShowUserProfileSidebar: {
      type: Boolean,
      required: true,
    },
    profileUserData: {
      type: Object,
      required: true,
    },
    profileUserMinimalData: {
      type: Object,
      required: true,
    },
    activeChatContactId: {
      type: String,
      default: null,
    },
    mqShallShowLeftSidebar: {
      type: Boolean,
      required: true,
    },
  },
  setup(props) {
    const perfectScrollbarSettings = {
      maxScrollbarLength: 150,
    }

    // Active tab
    const activeTab = ref('1v1')

    const resolveChatContact = userId => props.chatsContacts.find(contact => contact.id === userId)

    // Search Query
    const searchQuery = ref('')

    const searchFilterFunction = contact => (contact.userName || '').toLowerCase().includes(searchQuery.value.toLowerCase())
    const filteredChatsContacts = computed(() => props.chatsContacts.filter(searchFilterFunction))
    const filteredCommunityContacts = computed(() => props.communityContacts.filter(searchFilterFunction))
    const filteredContacts = computed(() => props.chatsContacts.filter(searchFilterFunction))

    // Selected mood label for the "Filter by Mood" button (null => show default $t label)
    const selectedMoodLabel = computed(() => {
      const mood = props.moodOptions.find(m => m.id === props.chatMoodId)
      return mood ? `${mood.name} ${mood.emoji}` : null
    })

    return {
      // Mood filter
      selectedMoodLabel,

      // Tab
      activeTab,

      // Search Query
      searchQuery,
      filteredChatsContacts,
      filteredCommunityContacts,
      filteredContacts,

      // UI
      resolveChatContact,
      perfectScrollbarSettings,
    }
  },
}
</script>
